/**
 * DialogueOverlay — Metal Gear codec-style portrait + typewriter dialogue.
 *
 * Shows character portrait on the left, name + role above,
 * text reveals character-by-character, auto-advances after display.
 */

import { useEffect, useState, useRef, useCallback } from "react";
import type { DialogueEvent } from "../../systems/MissionLogger";
import type { EncounterState } from "../../types/game";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8787";

// How long to show each line after it finishes typing
const HOLD_DURATION_MS = 3000;
// Characters per second for typewriter effect
const CHARS_PER_SEC = 40;

interface Props {
  dialogueQueue: DialogueEvent[];
  onDismiss: () => void;
  initialEncounter?: EncounterState;
}

export function DialogueOverlay({ dialogueQueue, onDismiss, initialEncounter }: Props) {
  const [displayedText, setDisplayedText] = useState("");
  const [isRevealing, setIsRevealing] = useState(false);
  const [portraitUrl, setPortraitUrl] = useState<string | null>(null);
  const [portraitError, setPortraitError] = useState(false);
  const timerRef = useRef<number>(0);
  const revealRef = useRef<number>(0);

  const currentLine = dialogueQueue[0] ?? null;

  // Resolve character info from encounter state
  const getCharacterInfo = useCallback(
    (speaker: string) => {
      if (!initialEncounter) return { name: speaker, role: "", characterId: "" };

      const speakerLower = speaker.toLowerCase();

      // Check parties (NPCs) — exact match first, then partial (GM often uses surname only)
      const party =
        initialEncounter.parties?.find((p) => p.name === speaker || p.id === speaker) ??
        initialEncounter.parties?.find((p) => p.name.toLowerCase().includes(speakerLower) || speakerLower.includes(p.name.toLowerCase()));
      if (party) {
        return {
          name: party.name,
          role: party.faction ?? "",
          characterId: party.id ?? party.name.toLowerCase().replace(/\s+/g, "-"),
        };
      }

      // Check crew — exact match first, then partial (GM often uses surname only)
      const crew =
        initialEncounter.crew?.find((c) => c.name === speaker || c.role === speaker) ??
        initialEncounter.crew?.find((c) => c.name.toLowerCase().includes(speakerLower) || speakerLower.includes(c.name.toLowerCase()));
      if (crew) {
        return {
          name: crew.name,
          role: crew.role ?? "",
          characterId: crew.name.toLowerCase().replace(/\s+/g, "-"),
        };
      }

      // Captain / player
      if (speakerLower.includes("captain") || speaker === "You") {
        return { name: "CAPTAIN", role: "Commanding Officer", characterId: "captain" };
      }

      return { name: speaker, role: "", characterId: speaker.toLowerCase().replace(/\s+/g, "-") };
    },
    [initialEncounter]
  );

  // Load portrait when speaker changes
  useEffect(() => {
    if (!currentLine) {
      setPortraitUrl(null);
      return;
    }

    const { characterId } = getCharacterInfo(currentLine.speaker);
    setPortraitError(false);

    // Don't try to load captain portrait (use insignia instead)
    if (characterId === "captain") {
      setPortraitUrl(null);
      return;
    }

    const url = `${API_BASE}/api/portrait/${characterId}`;
    setPortraitUrl(url);
  }, [currentLine?.speaker, getCharacterInfo]);

  // Typewriter reveal effect
  useEffect(() => {
    if (!currentLine) {
      setDisplayedText("");
      setIsRevealing(false);
      return;
    }

    const fullText = currentLine.text;
    let charIndex = 0;
    setDisplayedText("");
    setIsRevealing(true);

    const interval = 1000 / CHARS_PER_SEC;
    revealRef.current = window.setInterval(() => {
      charIndex++;
      setDisplayedText(fullText.slice(0, charIndex));

      if (charIndex >= fullText.length) {
        window.clearInterval(revealRef.current);
        setIsRevealing(false);

        // Auto-dismiss after hold duration
        timerRef.current = window.setTimeout(() => {
          onDismiss();
        }, HOLD_DURATION_MS);
      }
    }, interval);

    return () => {
      window.clearInterval(revealRef.current);
      window.clearTimeout(timerRef.current);
    };
  }, [currentLine, onDismiss]);

  if (!currentLine) return null;

  const { name, role, characterId } = getCharacterInfo(currentLine.speaker);
  const isCaptain = characterId === "captain";

  return (
    <div className="dialogue-overlay">
      <div className="dialogue-box">
        {/* Portrait */}
        <div className="dialogue-portrait">
          {isCaptain ? (
            <div className="dialogue-portrait-insignia">★</div>
          ) : portraitUrl && !portraitError ? (
            <img
              src={portraitUrl}
              alt={name}
              className="dialogue-portrait-img"
              onError={() => setPortraitError(true)}
            />
          ) : (
            <div className="dialogue-portrait-placeholder">
              {name.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="dialogue-portrait-scanline" />
        </div>

        {/* Text content */}
        <div className="dialogue-content">
          <div className="dialogue-speaker">
            <span className="dialogue-speaker-name">{name}</span>
            {role && <span className="dialogue-speaker-role">{role}</span>}
          </div>
          <div className="dialogue-text">
            {displayedText}
            {isRevealing && <span className="dialogue-cursor">▌</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
